import { useEffect, useState } from 'react'

/**
 * Banner de consentimento (LGPD) + Google Consent Mode v2 + carregamento do GTM.
 *
 * Padrão portado do educascale, mas TOTALMENTE ISOLADO:
 *  - Container GTM PRÓPRIO do SecBrain via `VITE_GTM_ID` (NUNCA o do educascale).
 *  - Chave de armazenamento própria (`secbrain_cookie_consent`).
 *
 * Arquitetura: o site só empurra eventos + sinais de consentimento para o
 * `dataLayer`. Os tags de verdade (GA4, Google Ads, Meta Pixel) ficam DENTRO
 * do container GTM e só disparam após consentimento (Consent Mode v2).
 *
 * Se `VITE_GTM_ID` estiver vazio, o componente é um no-op total: nenhum banner
 * é exibido, nenhum script é carregado, nenhum cookie é gravado.
 */

const GTM_ID = ((import.meta.env.VITE_GTM_ID as string | undefined) || '').trim()
const CONSENT_KEY = 'secbrain_cookie_consent'

type CookieConsent = {
  necessary: true
  analytics: boolean
  marketing: boolean
  updatedAt: string
}

type DataLayerWindow = Window & { dataLayer?: Array<Record<string, unknown>> }

function dl(): Array<Record<string, unknown>> {
  const w = window as DataLayerWindow
  w.dataLayer = w.dataLayer || []
  return w.dataLayer
}

function getStoredConsent(): CookieConsent | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY)
    return raw ? (JSON.parse(raw) as CookieConsent) : null
  } catch {
    return null
  }
}

function loadGtm() {
  if (!GTM_ID) return
  if (document.querySelector(`script[src*="${GTM_ID}"]`)) return
  dl().push({ event: 'gtm.js', 'gtm.start': Date.now() })
  const firstScript = document.getElementsByTagName('script')[0]
  const gtmScript = document.createElement('script')
  gtmScript.async = true
  gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`
  firstScript?.parentNode?.insertBefore(gtmScript, firstScript)
}

function applyConsent(consent: CookieConsent) {
  dl().push({
    event: 'cookie_consent_update',
    analytics_storage: consent.analytics ? 'granted' : 'denied',
    ad_storage: consent.marketing ? 'granted' : 'denied',
    ad_user_data: consent.marketing ? 'granted' : 'denied',
    ad_personalization: consent.marketing ? 'granted' : 'denied',
  })
  if (consent.analytics || consent.marketing) loadGtm()
}

export function CookieConsent() {
  const [visible, setVisible] = useState(false)
  const [showPrefs, setShowPrefs] = useState(false)
  const [analytics, setAnalytics] = useState(false)
  const [marketing, setMarketing] = useState(false)

  useEffect(() => {
    if (!GTM_ID) return // no-op total sem container próprio configurado

    const stored = getStoredConsent()
    if (stored) {
      applyConsent(stored)
      setAnalytics(stored.analytics)
      setMarketing(stored.marketing)
      return
    }

    // Consent Mode v2 — padrão NEGADO antes de qualquer tag
    dl().push({
      event: 'cookie_consent_default',
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    })
    setVisible(true)

    // Permite reabrir as preferências por um link com [data-open-cookie-preferences]
    const onReopen = (event: MouseEvent) => {
      const target = (event.target as HTMLElement | null)?.closest('[data-open-cookie-preferences]')
      if (!target) return
      event.preventDefault()
      const current = getStoredConsent()
      setAnalytics(Boolean(current?.analytics))
      setMarketing(Boolean(current?.marketing))
      setShowPrefs(true)
      setVisible(true)
    }
    document.addEventListener('click', onReopen)
    return () => document.removeEventListener('click', onReopen)
  }, [])

  if (!GTM_ID || !visible) return null

  const save = (a: boolean, m: boolean) => {
    const consent: CookieConsent = {
      necessary: true,
      analytics: a,
      marketing: m,
      updatedAt: new Date().toISOString(),
    }
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(consent))
    } catch {
      /* ignore */
    }
    applyConsent(consent)
    setVisible(false)
    setShowPrefs(false)
  }

  return (
    <div
      className="fixed bottom-5 right-5 z-[300] w-[min(520px,calc(100vw-40px))]"
      role="dialog"
      aria-live="polite"
      aria-label="Preferências de cookies"
    >
      <div className="rounded-2xl border border-white/12 bg-surface-950/95 p-5 shadow-2xl backdrop-blur-xl">
        <span className="mb-2 inline-flex text-[10px] font-bold uppercase tracking-[0.12em] text-brand-400">
          Privacidade
        </span>
        <h2 className="mb-2 font-serif text-xl leading-tight text-white">Cookies sob controle.</h2>
        <p className="text-sm leading-relaxed text-white/60">
          Usamos cookies essenciais para o site funcionar e, com sua autorização, cookies de
          analytics e marketing para medir desempenho e melhorar a experiência. Dados clínicos dos
          pacientes nunca passam por aqui.
        </p>
        <a
          href="/privacidade"
          className="mt-3 inline-flex text-[13px] font-semibold text-brand-400 hover:underline"
        >
          Ver política de privacidade
        </a>

        {showPrefs && (
          <div className="mt-4 grid gap-2.5">
            <label className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-3 opacity-70">
              <span>
                <strong className="block text-[13px] text-white">Essenciais</strong>
                <small className="mt-0.5 block text-xs leading-snug text-white/50">
                  Necessários para segurança, navegação e preferências.
                </small>
              </span>
              <input type="checkbox" checked disabled className="h-5 w-5 accent-brand-500" />
            </label>

            <label className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-3">
              <span>
                <strong className="block text-[13px] text-white">Analytics</strong>
                <small className="mt-0.5 block text-xs leading-snug text-white/50">
                  Entender visitas, cliques e conversões — sem vender seus dados.
                </small>
              </span>
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                className="h-5 w-5 accent-brand-500"
              />
            </label>

            <label className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-3">
              <span>
                <strong className="block text-[13px] text-white">Marketing</strong>
                <small className="mt-0.5 block text-xs leading-snug text-white/50">
                  Mensurar campanhas e otimizar anúncios do SecBrain.
                </small>
              </span>
              <input
                type="checkbox"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
                className="h-5 w-5 accent-brand-500"
              />
            </label>
          </div>
        )}

        <div className="mt-4 flex flex-wrap justify-end gap-2.5">
          <button
            type="button"
            onClick={() => save(false, false)}
            className="min-h-[40px] rounded-lg border border-white/16 bg-transparent px-4 text-[13px] font-bold text-white/80 transition hover:border-white/35 hover:bg-white/[0.06]"
          >
            Recusar
          </button>
          {!showPrefs ? (
            <button
              type="button"
              onClick={() => setShowPrefs(true)}
              className="min-h-[40px] rounded-lg border border-white/16 bg-transparent px-4 text-[13px] font-bold text-white/80 transition hover:border-white/35 hover:bg-white/[0.06]"
            >
              Personalizar
            </button>
          ) : (
            <button
              type="button"
              onClick={() => save(analytics, marketing)}
              className="min-h-[40px] rounded-lg border border-brand-500 bg-brand-500 px-4 text-[13px] font-bold text-surface-950 transition hover:bg-brand-600 hover:border-brand-600"
            >
              Salvar escolhas
            </button>
          )}
          <button
            type="button"
            onClick={() => save(true, true)}
            className="min-h-[40px] rounded-lg border border-brand-500 bg-brand-500 px-4 text-[13px] font-bold text-surface-950 transition hover:bg-brand-600 hover:border-brand-600"
          >
            Aceitar todos
          </button>
        </div>
      </div>
    </div>
  )
}
